<template>
  <modal
    :show="isVisible"
    :title="modalTitle"
    elem-id="modal_crud_so-report-attributes"
    size="small"
    :close-label="t('Cancelar')"
    :save-label="isDeleteMode ? (isDeleting ? t('Deletando') : t('Deletar')) : (isSaving ? t('Salvando') : t('Salvar'))"
    :save-fn="isDeleteMode ? handleDelete : handleSave"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <template v-if="isVisible">
      <div v-if="isDeleteMode" class="delete-confirmation">
        <p>{{ t('Tem certeza que deseja deletar este atributo de laudo?') }}</p>
        <p class="text-muted">{{ attributeToDelete?.so_ra_name }}</p>
      </div>

      <div v-else>
        <s-o-report-attributes-form
          ref="formRef"
          :attributeId="currentId"
          :isEditMode="isEditMode"
          @submit-success="handleFormSuccess"
          @submit-error="handleFormError"
        />
        <div v-if="isEditMode" class="delete-action">
          <Button
            data-testid="so-report-attributes-delete-button"
            severity="danger"
            outlined
            icon="pi pi-trash"
            :label="t('Deletar')"
            @click="enterDeleteConfirmation"
          />
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSOReportAttributes } from '../../composables/useSOReportAttributes';
import { eventBus } from '../../eventBus';
import SOReportAttributesForm from './SOReportAttributesForm.vue';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';

type Id = number | string;

interface FormInstance {
  resetForm: () => void;
  loadForEdit: () => Promise<void>;
  handleSubmit: () => Promise<void>;
  validateForm?: () => boolean;
}

interface ModalOpenPayload {
  id?: Id;
  data?: Record<string, unknown> | null;
  action?: string;
}

const { t } = useI18n();

const { remove } = useSOReportAttributes();

const isVisible = ref<boolean>(false);
const isEditMode = ref<boolean>(false);
const isDeleteMode = ref<boolean>(false);
const currentId = ref<Id | undefined>(undefined);
const attributeToDelete = ref<Record<string, unknown> | null>(null);
const formRef = ref<FormInstance | null>(null);
const isSaving = ref<boolean>(false);
const isDeleting = ref<boolean>(false);

const modalTitle = computed<string>(() => {
  if (isDeleteMode.value) {
    return t('Deletar Atributo de Laudo');
  } else if (isEditMode.value) {
    return t('Editar Atributo de Laudo');
  } else {
    return t('Adicionar Atributo de Laudo');
  }
});

const openForCreate = (): void => {
  log('SOReportAttributesModal', 'openForCreate', 'Opening modal for create');
  isVisible.value = true;
  isEditMode.value = false;
  isDeleteMode.value = false;
  currentId.value = undefined;

  nextTick(() => {
    if (formRef.value) {
      formRef.value.resetForm();
    }
  });
};

const openForEdit = async (id: Id): Promise<void> => {
  log('SOReportAttributesModal', 'openForEdit', 'Opening modal for edit', id);
  isVisible.value = true;
  isEditMode.value = true;
  isDeleteMode.value = false;
  currentId.value = id;

  await nextTick();

  if (formRef.value) {
    await formRef.value.loadForEdit();
  }
};

const openForDelete = async (id: Id, data: Record<string, unknown> | null = null): Promise<void> => {
  log('SOReportAttributesModal', 'openForDelete', 'Opening modal for delete', id);
  isVisible.value = true;
  isEditMode.value = false;
  isDeleteMode.value = true;
  currentId.value = id;
  attributeToDelete.value = data;
};

const enterDeleteConfirmation = (): void => {
  log('SOReportAttributesModal', 'enterDeleteConfirmation', 'Switching to delete confirmation', currentId.value);
  isEditMode.value = false;
  isDeleteMode.value = true;
};

const handleClose = (): void => {
  log('SOReportAttributesModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  isEditMode.value = false;
  isDeleteMode.value = false;
  currentId.value = undefined;
  attributeToDelete.value = null;
  isSaving.value = false;
  isDeleting.value = false;

  if (formRef.value) {
    formRef.value.resetForm();
  }
};

const handleSave = async (): Promise<void> => {
  log('SOReportAttributesModal', 'handleSave', 'Save button clicked');
  if (formRef.value) {
    isSaving.value = true;
    try {
      await formRef.value.handleSubmit();
    } finally {
      isSaving.value = false;
    }
  }
};

const handleDelete = async (): Promise<void> => {
  if (!currentId.value) {
    logWarn('SOReportAttributesModal', 'handleDelete', 'No ID to delete');
    return;
  }

  log('SOReportAttributesModal', 'handleDelete', 'Deleting report attribute', currentId.value);
  isDeleting.value = true;

  try {
    await remove(currentId.value);
    eventBus.emit('globalMessage.success', t('Atributo de laudo deletado com sucesso'));
    eventBus.emit('so-report-attributes.deleted');
    handleClose();
  } catch (error) {
    const err = error as Error;
    logError('SOReportAttributesModal', 'handleDelete', 'Error deleting report attribute', err);
    eventBus.emit('globalMessage.error', t('Erro ao deletar atributo de laudo') + ': ' + err.message);
  } finally {
    isDeleting.value = false;
  }
};

const handleFormSuccess = (payload?: unknown): void => {
  log('SOReportAttributesModal', 'handleFormSuccess', 'Form submitted successfully', payload);
  eventBus.emit('so-report-attributes.saved', payload);
  handleClose();
};

const handleFormError = (error: unknown): void => {
  logError('SOReportAttributesModal', 'handleFormError', 'Form submission error', error);
};

const handleModalOpen = async (data?: ModalOpenPayload): Promise<void> => {
  log('SOReportAttributesModal', 'handleModalOpen', 'Standard modal open event received', data);

  if (data?.action === 'delete' && data?.id) {
    await openForDelete(data.id, data.data || null);
  } else if (data?.id) {
    await openForEdit(data.id);
  } else {
    openForCreate();
  }
};

onMounted(() => {
  log('SOReportAttributesModal', 'onMounted', 'Component mounted, setting up event listeners');

  eventBus.on('modal.so-report-attributes.open', handleModalOpen);
  eventBus.on('modal.so-report-attributes.close', handleClose);
});

onBeforeUnmount(() => {
  log('SOReportAttributesModal', 'onBeforeUnmount', 'Component unmounting, cleaning up event listeners');

  eventBus.off('modal.so-report-attributes.open', handleModalOpen);
  eventBus.off('modal.so-report-attributes.close', handleClose);
});

defineExpose({
  openForCreate,
  openForEdit,
  openForDelete
});
</script>

<style scoped>
.delete-confirmation {
  padding: 20px;
  text-align: center;
}

.delete-confirmation p {
  margin-bottom: 10px;
  font-size: 16px;
}

.text-muted {
  color: #6c757d;
  font-style: italic;
}

.delete-action {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--app-bg-dark, #ccc);
}
</style>
